import * as zod from 'zod';

import { ActionRequiredError, Step } from '@sb/remote-control/types';
import { Expression } from '@sb/routine-runner';

export namespace RunLocateSkillStep {
  export const name = 'Locate';
  export const description = 'Locate via vision';
  export const librarySection = Step.LibrarySection.Vision;
  export const argumentKind = 'RunLocateSkill';

  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    skillId: zod.string().default(''),
    positionListName: zod.string().optional(),
    // Display-only name for the derived capture-pose Space item. Consumed by
    // `getVirtualSpaceItems`; the routine runner never sees Space-item names,
    // so this is intentionally not forwarded through `toRoutineRunner`.
    capturePoseName: zod.string().optional(),
    captureConfig: zod
      .object({
        saveEquipmentId: zod.string(),
        saveFilepath: Expression,
        saveMetadata: zod.boolean().default(false),
      })
      .optional(),
    // Deploy-time override of a text_prompt skill's saved prompt. Either a
    // literal string or an Expression resolved to a string at run time (e.g. a
    // routine variable). Empty = use the skill's saved prompt. Only meaningful
    // for text_prompt skills.
    textPromptOverride: zod.union([zod.string(), Expression]).optional(),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const validator: Step.Validator = ({ stepConfiguration }) => {
    const args = stepConfiguration?.args;

    if (args?.argumentKind !== argumentKind) {
      return;
    }

    if (!args.skillId) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'No Locate skill selected',
        fieldId: 'skillId',
      });
    }
  };

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    return {
      ...stepData,
      stepKind: 'RunLocateSkill',
      args: {
        skillId: args.skillId,
        positionListName: args.positionListName,
        captureConfig: args.captureConfig,
        textPromptOverride: args.textPromptOverride,
      },
    };
  };
}

RunLocateSkillStep satisfies Step.StepKindInfo;
